import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { MdOutlineNavigateBefore, MdOutlineNavigateNext } from "react-icons/md";
import { Heart } from "lucide-react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShareNodes } from "@fortawesome/free-solid-svg-icons";
import { FaRupeeSign } from "react-icons/fa";
import ShareModal from "./ShareModal";

const ProjectList = () => {
  const history = useHistory();
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentShareUrl, setCurrentShareUrl] = useState("");
  const [projects, setProjects] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const openSpotlightShareModal = useCallback((projectId) => {
    const baseUrl = window.location.origin;
    const fullUrl = `${baseUrl}/projectdetail/${projectId}`;
    setCurrentShareUrl(fullUrl);
    setShowShareModal(true);
  }, []);

  const closeShareModal = useCallback(() => {
    setShowShareModal(false);
  }, []);

  const copyLink = useCallback(() => {
    navigator.clipboard.writeText(currentShareUrl);
    alert("Link copied: " + currentShareUrl);
  }, [currentShareUrl]);

  const fetchProjects = async () => {
    try {
      const token = sessionStorage.getItem("accessToken");
      const formData = new FormData();
      if (token) {
        formData.append("user_id", token);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/cust_api/all_project_list`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setProjects(Array.isArray(response.data.data) ? response.data.data : []);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setProjects([]);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // Pagination logic
  const totalPages = Math.ceil(projects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const visibleProjects = projects.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  const formatPrice = (price) => {
    if (!price) return "";
    price = parseInt(price);

    if (price >= 10000000) {
      return parseFloat((price / 10000000).toFixed(1)) + " Cr";
    } else if (price >= 100000) {
      return parseFloat((price / 100000).toFixed(1)) + " L";
    } else if (price >= 1000) {
      return parseFloat((price / 1000).toFixed(1)) + " K";
    } else {
      return price.toString();
    }
  };

  const formatAverageProjectPrice = (price) => {
    if (!price) return "";

    if (typeof price === "string" && price.includes("-")) {
      const parts = price.split("-").map((p) => p.trim());
      return (
        <>
          {parts.map((p, idx) => (
            <span key={idx} className="inline-flex items-center">
              <FaRupeeSign className="inline-block mr-0.5 text-xs" />
              {formatPrice(p)}
              {idx === 0 && " - "}
            </span>
          ))}
        </>
      );
    }

    price = parseInt(price);
    if (isNaN(price)) return "";

    let formatted;
    if (price >= 10000000) {
      formatted = parseFloat((price / 10000000).toFixed(1)) + " Cr";
    } else if (price >= 100000) {
      formatted = parseFloat((price / 100000).toFixed(1)) + " L";
    } else if (price >= 1000) {
      formatted = parseFloat((price / 1000).toFixed(1)) + " K";
    } else {
      formatted = price.toString();
    }

    return (
      <span className="inline-flex items-center">
        <FaRupeeSign className="inline-block mr-0.5 text-xs" />
        {formatted}
      </span>
    );
  };

  // Add to favorites
  const addToFavoritesRecommendedProperty = async (projectId) => {
    if (!sessionStorage.getItem("accessToken")) {
      toast.error("Please login to add to favorites");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("user_id", sessionStorage.getItem("accessToken"));
      formData.append("project_id", projectId);

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/cust_api/add_favorite_project`,
        formData,
      );

      const favId = res.data?.favorite_id || projectId;

      setProjects((prev) =>
        prev.map((proj) =>
          proj._id === projectId
            ? { ...proj, is_favorite: true, favorite_id: favId }
            : proj,
        ),
      );
      fetchProjects();
    } catch (error) {
      toast.error("Failed to save the Project. Please try again.");
    }
  };

  // Remove from favorites
  const removeFromFavoritesRecommendedProperty = async (
    favoriteId,
    projectId,
  ) => {
    try {
      const formData = new FormData();
      formData.append("favorite_id", favoriteId);

      await axios.delete(
        `${process.env.REACT_APP_API_URL}/cust_api/remove_favorite_project`,
        {
          data: formData,
        },
      );

      setProjects((prev) =>
        prev.map((proj) =>
          proj._id === projectId
            ? { ...proj, is_favorite: false, favorite_id: null }
            : proj,
        ),
      );
      fetchProjects();
    } catch (error) {
      toast.error("Failed to remove the Project. Please try again.");
    }
  };

  return (
    <>
      <div className="min-h-screen bg-[#fbf3ec] p-4 sm:p-6 rounded-2xl mx-4 sm:mx-6 mb-6">
        <div className="flex flex-col space-y-6">
          <div className="flex items-center justify-between px-2">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 m-0">All Projects</h2>
            <span className="text-xs text-gray-500 font-medium">{projects.length} projects found</span>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {visibleProjects.map((project) => (
              <div
                key={project._id}
                className="bg-white rounded-2xl border border-[#eadfd6] shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col group"
              >
                {/* Image Section */}
                <div 
                  className="relative h-52 bg-slate-900 overflow-hidden cursor-pointer"
                  onClick={() => history.push(`/projectdetail/${project._id}`)}
                >
                  <img
                    src={project.cover_image}
                    alt={project.project_name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/50 to-transparent pointer-events-none" />

                  {/* Project Type Badge */}
                  <div className="absolute top-3 left-3">
                    <span className="px-2 py-1 text-[10px] font-semibold tracking-wide text-white rounded bg-[#7a1235] shadow">
                      {project.project_type || "Project"}
                    </span>
                  </div>

                  {/* Top Right Controls (Favorite & Share) */}
                  <div className="absolute top-3 right-3 flex items-center gap-2">
                    <button
                      type="button"
                      aria-label="Favorite"
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-white/90 backdrop-blur shadow hover:bg-white transition-transform active:scale-95"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!sessionStorage.getItem("accessToken")) {
                          toast.error("Please login to add to favorites");
                          return;
                        }
                        if (project.is_favorite) {
                          removeFromFavoritesRecommendedProperty(
                            project.favorite_id,
                            project._id,
                          );
                        } else {
                          addToFavoritesRecommendedProperty(project._id);
                        }
                      }}
                    >
                      <Heart
                        size={16}
                        color={project.is_favorite ? "#e11d48" : "#7a1235"}
                        fill={project.is_favorite ? "#e11d48" : "transparent"}
                        strokeWidth={2}
                      />
                    </button>
                    <button
                      type="button"
                      aria-label="Share"
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-white/90 backdrop-blur shadow hover:bg-white transition-transform active:scale-95 text-[#7a1235]"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openSpotlightShareModal(project._id);
                      }}
                    >
                      <FontAwesomeIcon icon={faShareNodes} className="text-xs" />
                    </button>
                  </div>
                </div>

                {/* Project Details Body */}
                <div className="p-4 flex flex-col flex-1 justify-between gap-3">
                  <div className="space-y-1">
                    <h3 
                      className="text-sm sm:text-base font-bold text-gray-900 truncate hover:text-[#7a1235] transition-colors cursor-pointer m-0"
                      onClick={() => history.push(`/projectdetail/${project._id}`)}
                    >
                      {project.project_name || "Project Name Unavailable"}
                    </h3>
                    <p className="flex items-center gap-1.5 text-xs text-gray-500 m-0">
                      <img
                        src="/image/address_icon.png"
                        alt="Location"
                        className="w-3.5 h-3.5 object-contain shrink-0"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <span className="truncate">{project.address_area || "No Address Provided"}</span>
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-[#eadfd6]">
                    <div className="text-xs font-semibold text-gray-700 truncate max-w-[50%]">
                      {project.congfigurations
                        ? project.congfigurations.includes("BHK")
                          ? project.congfigurations
                          : project.congfigurations
                              .split(",")
                              .map((c) => `${c.trim()} BHK`)
                              .join(", ")
                        : "Configurations on request"}
                    </div>
                    <div className="text-right">
                      {project.project_properties &&
                      project.project_properties.length > 0 ? (
                        (() => {
                          const prices = project.project_properties.map((p) =>
                            Number(p.price),
                          );
                          const minPrice = Math.min(...prices);
                          const maxPrice = Math.max(...prices);

                          return (
                            <div className="text-sm font-bold text-[#7a1235]">
                              {minPrice === maxPrice ? (
                                formatAverageProjectPrice(minPrice)
                              ) : (
                                <>
                                  {formatAverageProjectPrice(minPrice)} -{" "}
                                  {formatAverageProjectPrice(maxPrice)}
                                </>
                              )}
                            </div>
                          );
                        })()
                      ) : (
                        <span className="text-xs text-gray-400 font-medium">Price on request</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 pt-4">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`w-9 h-9 flex items-center justify-center rounded-lg border border-[#eadfd6] bg-white text-[#7a1235] transition-colors hover:bg-[#fbf3ec] ${
                  currentPage === 1 ? "opacity-40 cursor-not-allowed" : ""
                }`}
              >
                <MdOutlineNavigateBefore className="text-xl" />
              </button>

              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`min-w-[36px] h-9 px-2 text-sm rounded-lg flex items-center justify-center transition-colors ${
                    currentPage === i + 1
                      ? "bg-[#7a1235] text-white font-semibold"
                      : "text-gray-700 hover:bg-[#fbf3ec]"
                  }`}
                >
                  {i + 1}
                </button>
              ))}

              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className={`w-9 h-9 flex items-center justify-center rounded-lg border border-[#eadfd6] bg-white text-[#7a1235] transition-colors hover:bg-[#fbf3ec] ${
                  currentPage === totalPages ? "opacity-40 cursor-not-allowed" : ""
                }`}
              >
                <MdOutlineNavigateNext className="text-xl" />
              </button>
            </div>
          )}
        </div>
      </div>

      {showShareModal && (
        <ShareModal
          currentShareUrl={currentShareUrl}
          closeShareModal={closeShareModal}
          copyLink={copyLink}
        />
      )}
    </>
  );
};

export default ProjectList;